import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Grid, Paper, Tooltip as MuiTooltip, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const COLORS = {
    Ok: '#4caf50',
    Warning: '#ff9800',
    Critical: '#f44336'
};

// --- DONNÉES SIMULÉES (MOCK DATA) ---

const mockComplianceMeasures = [
    { name: '1. Code de Conduite', score: 100, status: 'Ok' },
    { name: '2. Dispositif Alerte', score: 80, status: 'Ok' },
    { name: '3. Cartographie Risques', score: 60, status: 'Warning' },
    { name: '4. Évaluation Tiers', score: 40, status: 'Critical' },
    { name: '5. Contrôles Comptables', score: 70, status: 'Warning' },
    { name: '6. Formation', score: 90, status: 'Ok' },
    { name: '7. Régime Disciplinaire', score: 100, status: 'Ok' },
    { name: '8. Contrôle Interne', score: 50, status: 'Warning' },
];

const mockRiskMap = [
    { subject: 'Corruption Public', A: 120, B: 110, fullMark: 150 },
    { subject: 'Cadeaux & Invit.', A: 98, B: 130, fullMark: 150 },
    { subject: 'Conflit Intérêt', A: 86, B: 130, fullMark: 150 },
    { subject: 'Sponsoring', A: 99, B: 100, fullMark: 150 },
    { subject: 'Facilitation', A: 85, B: 90, fullMark: 150 },
    { subject: 'Intermédiaires', A: 65, B: 85, fullMark: 150 },
];

const mockKYCStatus = [
    { stage: 'Screening', count: 150 },
    { stage: 'Due Diligence', count: 40 },
    { stage: 'Validation', count: 20 },
    { stage: 'Reject', count: 5 }
];

// --- SOUS-COMPOSANTS ---

const MeasuresTab = () => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>Maturité des 8 Mesures Sapin II</Typography>
        <Grid container spacing={2}>
            {mockComplianceMeasures.map((measure) => (
                <Grid item xs={12} sm={6} md={3} key={measure.name}>
                    <Paper
                        sx={{
                            p: 2,
                            borderLeft: `6px solid ${COLORS[measure.status] || '#ccc'}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle2">{measure.name}</Typography>
                            <Typography variant="h4">{measure.score}%</Typography>
                        </Box>
                        {measure.status === 'Critical' && <InfoIcon color="error" />}
                    </Paper>
                </Grid>
            ))}
        </Grid>
    </Box>
);

const RiskMapTab = () => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>Cartographie des Risques de Corruption</Typography>
        <Paper sx={{ p: 2, height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={mockRiskMap}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis />
                    <Radar name="Risque Brut" dataKey="B" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                    <Radar name="Risque Résiduel" dataKey="A" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                    <Legend />
                    <Tooltip />
                </RadarChart>
            </ResponsiveContainer>
        </Paper>
    </Box>
);

const KYCTab = () => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>État des Due Diligence Tiers (KYC)</Typography>
        <Paper sx={{ p: 2, height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={mockKYCStatus}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3f51b5" name="Nombre de Tiers" />
                </BarChart>
            </ResponsiveContainer>
        </Paper>
    </Box>
);

const Sapin2Dashboard = () => {
    const [tabIndex, setTabIndex] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabIndex} onChange={handleTabChange} >
                    <Tab label="8 Mesures (KPI)" />
                    <Tab label="Cartographie" />
                    <Tab label="KYC Tiers" />
                </Tabs>
            </Box>
            <Box sx={{ p: 2 }}>
                {tabIndex === 0 && <MeasuresTab />}
                {tabIndex === 1 && <RiskMapTab />}
                {tabIndex === 2 && <KYCTab />}
            </Box>
        </Box>
    );
};

export default Sapin2Dashboard;
